/**
 * PayPalCheckout — renders PayPal smart buttons for wallet top-up.
 * Props:
 *   amountTND  {number}   Amount in Tunisian Dinar (displayed to user)
 *   onSuccess  {function} Called after wallet is credited
 *   onError    {function} Called with error message string
 *
 * Note: PayPal sandbox doesn't support TND, so transactions are processed
 * in USD. In production with a live account, switch PAYPAL_MODE=live and
 * the backend converts the currency accordingly.
 */
import React, { useState, useEffect } from 'react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Box, Alert, CircularProgress, Typography } from '@mui/material';
import axios from 'axios';

/* USD/TND rough exchange rate for display purposes (not for billing) */
const TND_TO_USD = 0.32;

export default function PayPalCheckout({ amountTND, onSuccess, onError }) {
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null); // { clientId, mode, enabled }
  const [settingsError, setSettingsError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios
      .get('/api/payment-settings/public', { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        if (!data.paypal?.enabled) {
          setSettingsError('PayPal payments are currently disabled.');
          return;
        }
        if (!data.paypal?.clientId) {
          setSettingsError('PayPal is not configured yet. Please contact support.');
          return;
        }
        setSettings({ clientId: data.paypal.clientId, mode: data.paypal.mode ?? 'sandbox' });
      })
      .catch(() => setSettingsError('Failed to load payment configuration.'));
  }, []);

  const createOrder = async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        '/api/payments/paypal/create-order',
        { amountTND: Number(amountTND) },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!data.success) throw new Error(data.message || 'Failed to create PayPal order');
      return data.orderId;
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'PayPal order creation failed';
      setErrorMsg(msg);
      onError && onError(msg);
      throw err; // re-throw to abort PayPal flow
    } finally {
      setLoading(false);
    }
  };

  const onApprove = async (data) => {
    setLoading(true);
    setErrorMsg('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        '/api/payments/paypal/capture',
        { paypalOrderId: data.orderID },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.data.success) throw new Error(res.data.message || 'Capture failed');
      onSuccess && onSuccess({ amountTND, paypalOrderId: data.orderID });
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'PayPal capture failed';
      setErrorMsg(msg);
      onError && onError(msg);
    } finally {
      setLoading(false);
    }
  };

  const onPayPalError = (err) => {
    const msg = err?.message || 'PayPal encountered an error';
    setErrorMsg(msg);
    onError && onError(msg);
  };

  if (settingsError) {
    return <Alert severity="warning" sx={{ borderRadius: '12px' }}>{settingsError}</Alert>;
  }
  if (!settings) {
    return <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>;
  }

  return (
    <PayPalScriptProvider
      options={{
        'client-id': settings.clientId,
        currency: 'USD',
        intent: 'capture',
        components: 'buttons',
      }}
    >
      <Box>
        {loading && (
          <Box display="flex" justifyContent="center" mb={2}>
            <CircularProgress size={32} />
          </Box>
        )}

        {errorMsg && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setErrorMsg('')}>
            {errorMsg}
          </Alert>
        )}

        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '16px',
            p: 2,
            background: (t) => t.palette.background.paper,
          }}
        >
          <Typography variant="body2" color="text.secondary" textAlign="center" mb={2}>
            Charging ~${(amountTND * TND_TO_USD).toFixed(2)} USD ≈ {amountTND} TND
          </Typography>

          <PayPalButtons
            style={{ layout: 'vertical', shape: 'pill', color: 'gold', label: 'pay' }}
            disabled={loading}
            createOrder={createOrder}
            onApprove={onApprove}
            onError={onPayPalError}
            onCancel={() => setErrorMsg('Payment cancelled')}
          />
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          display="flex"
          alignItems="center"
          justifyContent="center"
          mt={1.5}
        >
          Secured by PayPal
        </Typography>
      </Box>
    </PayPalScriptProvider>
  );
}
